import { Fragment, PropsWithChildren, useEffect, useState } from "react";
import { Button, Hero } from "react-bulma-components";
import { useTranslation } from "react-i18next";
import { BoardAccessResult, checkBoardAccess } from "../../models/board";
import { homeRoute, useRouteContext } from "../../features/route/RouteProvider";
import { ContentLoader } from "../ContentLoader";
import { RetroHeroHeader } from "../RetroHeroHeader";
import { BoardInviteModal } from "./BoardInviteModal";
import { BoardRequestInviteView } from "./BoardRequestInviteView";

interface BoardMemberGateProps {
  userUid: string;
  boardUid: string;
}

export function BoardMemberGate({ userUid, boardUid, children }: PropsWithChildren<BoardMemberGateProps>) {
  const { t } = useTranslation();
  const [, setRoute] = useRouteContext();
  const [access, setAccess] = useState<BoardAccessResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    checkBoardAccess(boardUid)
      .then((result) => {
        if (!cancelled) {
          setAccess(result);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
          setError(true);
        }
      });
    return () => { cancelled = true; };
  }, [boardUid]);

  if (loading) {
    return (
      <Fragment>
        <Hero size="thirdheight" color="dark">
          <RetroHeroHeader />
        </Hero>
        <ContentLoader />
      </Fragment>
    );
  }

  if (error) {
    return (
      <section className="hero is-fullheight">
        <div className="hero-body">
          <div className="container has-text-centered">
            <p className="title is-4">{t("error.boardAccessFailed")}</p>
            <Button color="primary" onClick={() => setRoute(homeRoute)}>{t("error.goBack")}</Button>
          </div>
        </div>
      </section>
    );
  }

  if (!access || !access.exists) {
    return (
      <section className="hero is-fullheight">
        <div className="hero-body">
          <div className="container has-text-centered">
            <p className="title is-4">{t("error.boardNotFound")}</p>
            <Button color="primary" onClick={() => setRoute(homeRoute)}>{t("error.goBack")}</Button>
          </div>
        </div>
      </section>
    );
  }

  if (!access.isMember) {
    return (
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-content">
          <section className="section">
            <div className="container">
              {access.isPrivate
                ? <BoardRequestInviteView
                    boardUid={boardUid}
                    boardTitle={access.title ?? ""}
                    hasInvite={access.hasInvite}
                    onDecline={() => setRoute(homeRoute)}
                    onRequested={() => setAccess({ ...access, hasInvite: true })}
                  />
                : <BoardInviteModal
                    boardUid={boardUid}
                    boardTitle={access.title ?? ""}
                    userUid={userUid}
                    onDecline={() => setRoute(homeRoute)}
                    onJoined={() => setAccess({ ...access, isMember: true })}
                  />
              }
            </div>
          </section>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
